/**
 * Run `fn` every `ms` ms — but only while the tab is visible.
 *
 * - Skips ticks when `document.visibilityState !== 'visible'`. The
 *   underlying interval keeps firing (so the cadence stays predictable
 *   when the tab comes back), it just no-ops while hidden.
 * - On `hidden → visible` transition, fires `fn` once immediately so
 *   the UI catches up after a long backgrounded period.
 * - Returns a cleanup that clears the interval and detaches the
 *   visibility listener.
 *
 * Introduced 2026-05-18 to stop the global rate-limit storm where
 * backgrounded tabs (videos page mid-ingest, knowledge-agent chats)
 * kept hammering /api/v1/* every few seconds for hours.
 */
export function setVisibilityAwareInterval(fn: () => void, ms: number): () => void {
  const tick = (): void => {
    if (typeof document === 'undefined' || document.visibilityState === 'visible') {
      fn();
    }
  };
  const intervalId = window.setInterval(tick, ms);
  const onVisibility = (): void => {
    if (document.visibilityState === 'visible') {
      fn();
    }
  };
  document.addEventListener('visibilitychange', onVisibility);
  return () => {
    window.clearInterval(intervalId);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
